import { Injectable } from '@angular/core';

import { DprActivityGridData } from '@dpr-details/models/grid';
import { ProjectSetup, UserPreferences } from '@shared/models';
import {
  Activity,
  ActivityGroup,
  ActivityStream,
  Location,
} from '@shared/models/project-setup';
import { convertDateToTimeString, defaultStartTime } from '@shared/utils';
import { DprActivitiesGridService } from './grid';
import { ActivitySelectionVersion } from '@shared/enums';

@Injectable()
export class DprActivityCreateService {
  constructor(private dprActivitiesGridService: DprActivitiesGridService) {}

  createActivity(
    dprDetailId: number | string,
    lastRow: DprActivityGridData,
    activityStream: ActivityStream,
    projectSetup: ProjectSetup,
    userPreferences: UserPreferences
  ): Partial<DprActivityGridData> {
    let activityGroup: ActivityGroup;
    let activity: Activity;
    let location: Location;

    const activitySelectionVersion = userPreferences.activitySelectionVersion;

    if (activitySelectionVersion !== ActivitySelectionVersion.Blank) {
      activityGroup = this.computeActivityGroup(
        activityStream.id,
        lastRow?.activityGroupId,
        projectSetup.activityGroupList
      );

      activity = this.computeActivity(
        activityGroup?.id,
        lastRow?.activityId,
        projectSetup.activityList,
        activitySelectionVersion
      );

      location = this.computeLocation(
        activity?.id,
        lastRow?.locationId,
        projectSetup.locationList,
        projectSetup.activityList,
        activitySelectionVersion
      );
    }

    // reset the activity group if no activity could be selected for the activity group
    // skip reset if the activity group is the only available option
    if (
      !activity &&
      this.dprActivitiesGridService.getActivityGroupOptions(
        activityStream.id,
        projectSetup.activityGroupList
      ).length > 1
    ) {
      activityGroup = null;
    }

    const start = this.computeStart(lastRow, activityStream);
    const finish = activity?.isMilestone ? start : null;

    const result: Partial<DprActivityGridData> = {
      dprDetailId,
      activityGroupId: activityGroup?.id,
      activityId: activity?.id,
      locationId: location?.id,
      start,
      finish,
      comment: '',
      dprSubActivities: [],
      dprActivityCustomDataList: [],
      dprActivityAttachments: [],
      parallelActivityType: activityStream.parallelActivityType,
      canBeDeleted: true,
    };

    return result;
  }

  //#region Private methods

  /**
   * @description Try to preselect an active activity group in the following order:
   * 1. If the last selected activity group is still an option and it is active, select it
   * 2. If there is just one option available, select it
   */
  private computeActivityGroup(
    activityStreamId: number,
    activityGroupId: number,
    activityGroups: ActivityGroup[]
  ): ActivityGroup {
    const activityGroup = activityGroups.find((x) => x.id === activityGroupId);

    // return last selected activity
    if (activityGroup?.isActive) {
      return activityGroup;
    }

    const options = this.dprActivitiesGridService.getActivityGroupOptions(
      activityStreamId,
      activityGroups
    );

    return options.length === 1 ? options[0] : null;
  }

  /**
   * @description Try to preselect an active activity in the following order:
   * 1. If the last selected activity is still an option and it is active, select it
   * 2. If there is just one option available, select it
   * 3. Try to find the next active option based on priority
   */
  private computeActivity(
    activityGroupId: number,
    activityId: number,
    activities: Activity[],
    activitySelectionVersion: ActivitySelectionVersion
  ): Activity {
    if (activitySelectionVersion === ActivitySelectionVersion.RepeatLast) {
      return activities.find((ac) => ac.id === activityId);
    }

    const options = this.dprActivitiesGridService.getActivityOptions(
      activityGroupId,
      activities
    );

    const activityIndex = options.findIndex((x) => x.id === activityId);

    if (activityIndex !== -1 && activityIndex + 1 < options.length) {
      return options[activityIndex + 1];
    }

    const activity = options.find((x) => x.id === activityId);

    if (activity) {
      return activity;
    }

    if (options.length === 1) {
      return options[0];
    }

    const priority = activities.find((x) => x.id === activityId)?.priority;

    return options.find((x) => x.priority > priority);
  }

  /**
   * @description Try to preselect an active location in the following order:
   * 1. If the last selected location is still an option, select it
   * 2. If there is just one option available, select it
   */
  private computeLocation(
    activityId: number,
    locationId: number,
    locations: Location[],
    activities: Activity[],
    activitySelectionVersion: ActivitySelectionVersion
  ): Location {
    if (activitySelectionVersion === ActivitySelectionVersion.RepeatLast) {
      return locations.find((l) => l.id === locationId);
    }
    const options = this.dprActivitiesGridService.getLocationOptions(
      activityId,
      locations,
      activities
    );

    const location = options.find((x) => x.id === locationId);

    if (location) {
      return location;
    }

    return options.length === 1 ? options[0] : null;
  }

  /**
   * @description Computes the start time based on the previous row finish time
   */
  private computeStart(
    previousRow: DprActivityGridData,
    activityStream: ActivityStream
  ): string {
    if (previousRow?.finish) {
      return previousRow?.finish;
    }

    // for primary activities if it is the first row added, assign default value
    if (activityStream?.parallelActivityType === 0 && !previousRow) {
      return convertDateToTimeString(defaultStartTime());
    }

    return null;
  }

  //#endregion
}
